import { Injectable, NgZone, inject } from '@angular/core';
import { Observable, Subject, merge, timer, Subscription, fromEvent } from 'rxjs';
import { throttleTime, startWith } from 'rxjs/operators';

export interface IdleTimeoutEvent {
  time: number;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class IdleTimerService {
  private idleEvents$ = new Subject<IdleTimeoutEvent>();
  private timerSubscriptions: Subscription[] = [];
  private activitySubscription = new Subscription();
  private timers: IdleTimeoutEvent[] = [];
  private zone = inject(NgZone);

  setTimers(timers: IdleTimeoutEvent[]): Observable<IdleTimeoutEvent> {
    this.timers = timers;
    this.clearTimers();
    this.startTimers();
    this.initActivityListeners();
    return this.idleEvents$.asObservable();
  }

  stop(): void {
    this.clearTimers();
    this.activitySubscription.unsubscribe();
  }

  private startTimers(): void {
    this.timers.forEach(t => {
      const sub = timer(t.time * 1000).subscribe(() => {
        this.idleEvents$.next(t);
      });
      this.timerSubscriptions.push(sub);
    });
  }

  private clearTimers(): void {
    this.timerSubscriptions.forEach(sub => sub.unsubscribe());
    this.timerSubscriptions = [];
  }

  private initActivityListeners(): void {
    if(!this.activitySubscription.closed) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = new Subscription();
    }
    this.clearTimers();
    this.startTimers();
    this.zone.runOutsideAngular(() => {
      const activity$ = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'keydown'),
        fromEvent(window, 'scroll'),
        fromEvent(window, 'pointerdown')
      ).pipe(throttleTime(1000));

      this.activitySubscription.add(activity$
        .subscribe(() => {
          this.zone.run(() => {
            this.clearTimers();
            this.startTimers();
          });
        }));
    });
  }
}
